import * as React from 'react';
import moment from 'moment';

import { ResourceRelationsService, ALERT, IAuthorizationsInitDataReply, IIdNamePair } from '../services/ResourceRelationsService';

import lang from './Relations/RelationsLang';
import { StylesProvider, createGenerateClassName } from '@material-ui/core';
import { IEditingIAuthorizationRelationData, IEditRowControlResult } from './RelationAuthorizations/AuthorizationRelationEditRow';
import { IAuthorizationRelationData, IAuthorizationRelationRawData } from './RelationAuthorizations/AuthorizationDomainModel';
import UserAuthorizationsWindow, { IUserAuthorizationsWindowConfig } from './RelationAuthorizations/UserAuthorizationsWindow';
import AuthorizationInsertForm from './RelationAuthorizations/AuthorizationInsertForm';
import AuthUserAuthorizationsService, { IUserAuthorizationDataRow, IDictionary, IUserAuthorizationData, IUserAuthorizationLoadingData } from './RelationAuthorizations/AuthUserAuthorizationsService';
import { ILevelPermission } from './RelationAuthorizations/AuthorizationLevelControl';
import { RequestControl, IRelationRowTable, IAllRequest, IEmployeeEntity } from './Relations/RequestControl';
import HtmlRootControl from './Relations/HtmlRootControl';
import { OptionType } from '../controls/EmployeesComboboxMI';
import { IConfig } from './RelationAuthorizations/Models';
import { IIssue } from './Relations/IssueForRelations';
import { LogFiltersBuildier } from './Relations/utils/LogFiltersBuildier';

import AuthorizationRelationEditRowDialog from './RelationAuthorizations/AuthorizationRelationEditRow';
import AutorizationRequestContent from './RelationAuthorizations/AutorizationRequestContent';

import "./Relations/Relations.css";

export interface IAuthorizationsData {
	employees: IEmployeeEntity[]
}

export interface IProps {

	onSet?: (v: IAuthorizationsData) => void,

	name: string;
	value: string;
	issue: IIssue;

	isValidField: boolean;

	forAcceptant: boolean;
	subordinatesTree: boolean;
	editCurrentEmployee: boolean;

	defaultEmployee: string;

	blockInsert: boolean;
	blockEditRows: boolean;
	blockCloneRows: boolean;
	blockDeleteRows: boolean;

	blockChangeEmployees: boolean;

	currentAuthorizationsHiddenInsertButton: boolean;
	currentAuthorizationsHiddenEditButton: boolean;
	currentAuthorizationsFilterId: string;
	currentAuthorizationsTable: IUserAuthorizationsWindowConfig;

	config: IConfig,
}

interface IState {
	logSystemEnabled: boolean,
	editingRow: IEditingIAuthorizationRelationData;
	allAuthorizationLevels: ILevelPermission[],

	rowsPerUsers: IDictionary<IUserAuthorizationData>,
	loading: IDictionary<IUserAuthorizationLoadingData>,

	selectedEmployees: OptionType[],
	selectedBusinessProcess: OptionType[],
	selectedDataSets: OptionType[],
}

const generateClassName = createGenerateClassName({
	productionPrefix: 'authorizationRelationControl',
});

export default class AuthorizationRelationControl extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			logSystemEnabled: false,

			editingRow: null,

			allAuthorizationLevels: [],
			rowsPerUsers: {},
			loading: {},

			selectedEmployees: [],
			selectedBusinessProcess: [],
			selectedDataSets: [],
		};

		this.resourceRelationsService = new ResourceRelationsService();
		this.insertForm = React.createRef();
		this.table = React.createRef();
		this.rootControl = React.createRef<HTMLDivElement>();
	}

	resourceRelationsService: ResourceRelationsService;
	table: React.RefObject<RequestControl>;
	insertForm: React.RefObject<AuthorizationInsertForm>;
	rootControl: React.RefObject<HTMLDivElement>;

	componentDidMount() {
		new ResourceRelationsService().getAuthorizationsInitData(this.props.issue.id, (data) => {

			let authorizations: IAuthorizationRelationData[] = [];
			if (!this.props.value) {
				authorizations = this.parseTableData(data);
			}
			else {
				var tableResult: IAuthorizationRelationData[] = JSON.parse(this.props.value);
				authorizations = tableResult;
			}

			var defaultUsers: OptionType[] = [null];

			if (authorizations.length == 0) {
				defaultUsers = this.getDefaultUsers(data);
			}

			var usersToLoad = defaultUsers.concat(authorizations.map(x => {
				if (!x?.employee?.id)
					return null;

				return {
					value: x.employee.id.toString(),
					label: x.employee.name,
					kind: "Employee",
				}
			}))
			this.loadUsers(usersToLoad);

			let levels: ILevelPermission[] = data.authorizationLevels.map(x => {
				return {
					id: x.id,
					name: x.name,
					value: x.id.toString(),
					label: x.name,
				}
			});

			this.setState({
				allAuthorizationLevels: levels,
				logSystemEnabled: data.logSystemEnabled,
				selectedEmployees: defaultUsers.filter(x => !!x),
			});
		});

		HtmlRootControl(this.rootControl, {
			canSubmit: () => {
				if (this.insertForm.current && this.insertForm.current.hasChanges()) {
					return {
						warningMessage: lang.authorizationControlHasChanges(),
					};
				};
			}
		});
	}

	getDefaultUsers(data: IAuthorizationsInitDataReply): OptionType[] {
		let defaultUser: OptionType[] = [];
		if (this.props.defaultEmployee === "<employeeAssets>") {
			defaultUser = this.props.issue.employeeAssets.map(x => {
				return {
					value: x.id.toString(),
					label: x.label,
					kind: "Employee",
				};
			});
		}
		else if (this.props.defaultEmployee === "<current>" && data.currentUser) {
			defaultUser.push({
				value: data.currentUser.id.toString(),
				label: data.currentUser.name,
				kind: "Employee",
			});
		}

		if (!this.props.config.fields.employee.multiple) {
			return [defaultUser[0]]
		}
		if (defaultUser.length == 0) {
			return [null];
		}

		return defaultUser;
	}

	loadUsers(users: OptionType[]) {
		var rowsPerUsers = { ...this.state.rowsPerUsers }
		var loading = { ...this.state.loading }

		for (var i = 0; i < users.length; i++) {
			this._loadUser(loading, rowsPerUsers, users[i]);
		}

		this.setState({
			rowsPerUsers: rowsPerUsers,
			loading: loading
		});
	}

	_loadUser(loading: IDictionary<IUserAuthorizationLoadingData>, rowsPerUsers: IDictionary<IUserAuthorizationData>, user: OptionType) {
		if (!user?.value)
			return;

		if (rowsPerUsers[user.value])
			return;

		var service = new AuthUserAuthorizationsService();
		var fields = this.props.config.fields;

		if (!loading[user.value]) {
			loading[user.value] = {
				forDataSet: false,
				forBusinessProcess: false,
			}
		}
		let fb = new LogFiltersBuildier(this.props.currentAuthorizationsTable.filters, this.props.issue);
		if (!fields.dataSet.hidden)
		{
			if (loading[user.value] && !(loading[user.value] as IUserAuthorizationLoadingData).forDataSet)
			{
				loading[user.value].forDataSet = true;
				service.getRelatedDataSetsResources(
					this.props.issue?.id,
					user.value,
					this.props.currentAuthorizationsFilterId,
					null,
					fb.buildForDataSets(),
					(data, status) => {
						this.setRelatedResources(user.value, data, "forDataSet", status);
					}
				);
			}
			
		}
		if (!fields.businessProcess.hidden) {
			if (loading[user.value] && !(loading[user.value] as IUserAuthorizationLoadingData).forBusinessProcess)
			{
				loading[user.value].forBusinessProcess = true;
				service.getRelatedBusinessProcessResources(
					this.props.issue?.id,
					user.value,
					this.props.currentAuthorizationsFilterId,
					null,
					fb.buildForBusinessProcess(),
					(data, status) => {
						this.setRelatedResources(user.value, data, "forBusinessProcess", status);
					}
				);
			}
		}
	}

	setRelatedResources(
		userId: string,
		data: IUserAuthorizationDataRow[],
		name: keyof IUserAuthorizationData,
		status: 'Success' | 'NotPermission' | 'EmployeeNotFound'
	) {
		const rowsPerUsers = { ...this.state.rowsPerUsers };
		const loading = { ...this.state.loading };

		let lodingObj = loading[userId];
		lodingObj[name] = false;

		let rowsPerUser: IUserAuthorizationData = rowsPerUsers[userId];

		if (!rowsPerUser) {
			rowsPerUser = {
				forBusinessProcessStatus: null,
				forBusinessProcess: null,
				forDataSetStatus: null,
				forDataSet: null,
			};
		} else {
			rowsPerUser = { ...rowsPerUser };
		}
		rowsPerUser[name] = data as any;
		rowsPerUser[name + "Status"] = status as any;
		rowsPerUsers[userId] = rowsPerUser;

		this.setState({
			rowsPerUsers: rowsPerUsers,
			loading: loading
		});
	}

	parseTableData(dataFromApi: IAuthorizationsInitDataReply): IAuthorizationRelationData[] {
		let result: IAuthorizationRelationData[] = null;
		if (dataFromApi.value && Array.isArray(dataFromApi.value)) {
			result = dataFromApi.value.map(initItemData => {
				
				let item: IAuthorizationRelationData = {
					id: initItemData.id,
					employee: initItemData.employee,
					resource: initItemData.resource,
					level: initItemData.level,
					from: initItemData.from ? moment(initItemData.from) : null,
					to: initItemData.to ? moment(initItemData.to) : null,
					deleted: initItemData.deleted,
					relationId: initItemData.relationId,
					key: null,
					errorMessages: initItemData.errorMessages,
				};

				return item;
			});

			this.checkAuthorizesInLog(result);
			this.table.current.push(result);
		}

		return result;
	}

	clear() {
		this.insertForm.current.clear();
	}
	
	checkAuthorizesInLog(table: IAuthorizationRelationData[]) {
		this.resourceRelationsService.checkAuthorizesInLog(table, () => {

			let rows: IAuthorizationRelationData[] = this.table.current.getRows();
			for (let i = 0; i < rows.length; i++) {
				let row = rows[i];
				row.alert = this.resourceRelationsService.getAuthorizationAlertCode(row);
			}
			this.table.current.setRows(rows);
		})
	}

	getLevelByName(name: string): IIdNamePair {
		if (!name)
			return null;

		var exist = this.state.allAuthorizationLevels.filter(x => x.name == name)[0];
		if (exist)
			return exist;

		return {
			id: -1,
			name: name,
		}
	}

	render() {

		const fields = this.props.config.fields;

		return (<StylesProvider generateClassName={generateClassName} >
			<div className="root-relation-resource">
				<AuthorizationInsertForm
					ref={this.insertForm}

					selectedEmployees={this.state.selectedEmployees}
					onChangeSelectedEmployees={(v) => {
						this.setState({ selectedEmployees: v })
						this.loadUsers(v);
					}}
					employeesFilters={{
						filterId: fields.employee.filterId,
						forAcceptant: this.props.forAcceptant,
						subordinatesTree: this.props.subordinatesTree,
						maxRows: fields.employee.maxRows,
						loadOnFocus: fields.employee.loadOnFocus,
					}}

					selectedDataSets={this.state.selectedDataSets}
					onChangeSelectedDataSets={(v) => this.setState({ selectedDataSets: v })}

					selectedBusinesProcesses={this.state.selectedBusinessProcess}
					onChangeSelectedBussinesProcesses={(v) => this.setState({ selectedBusinessProcess: v })}

					table={this.table.current}

					checkAuthorizesInLog={this.checkAuthorizesInLog.bind(this)}

					blockInsert={this.props.blockInsert}

					rowsPerUsers={this.state.rowsPerUsers}
					loading={this.state.loading}

					blockChangeEmployees={this.props.blockChangeEmployees}

					hiddenCurrentAuthorizationsButton={this.props.currentAuthorizationsHiddenInsertButton}

					allAuthorizationLevels={this.state.allAuthorizationLevels}

					getLevelByName={(name: string) => this.getLevelByName(name)}

					config={this.props.config}
					currentAuthorizationsTable={this.props.currentAuthorizationsTable}
				/>
				<div>
					{this.renderTable()}
				</div>
			</div>
		</StylesProvider>)
	}

	handleEditClick(event: any, row: IAuthorizationRelationData) {
		let r = { ...row, copy: false };
		this.setState({ editingRow: r });
	}

	handleCopyClick(event: any, row: IAuthorizationRelationData) {
		let r = { ...row, copy: true };
		this.setState({ editingRow: r });
	}

	handleTableInit(requests: IAllRequest) {
		var rows: IAuthorizationRelationData[] = [];
		Object.values(requests).map(x => rows = rows.concat(x.request as IAuthorizationRelationData[]));
		var emptyAlerts = rows.filter(r => !r.alert);
		if (emptyAlerts.length != 0)
			this.checkAuthorizesInLog(emptyAlerts);
	}

	renderTable() {
		return <div className="authorization-relation-control" ref={this.rootControl}>
			<h6>
				{lang.get("applicationScope")}
			</h6>
			<AutorizationRequestContent

				table={this.table}

				name={this.props.name}
				value={this.props.value}

				config={this.props.config}
				blockCloneRows={this.props.blockCloneRows}
				blockDeleteRows={this.props.blockDeleteRows}
				blockEditRows={this.props.blockEditRows}
				blockInsert={this.props.blockInsert}
				editCurrentEmployee={this.props.editCurrentEmployee}
				forAcceptant={this.props.forAcceptant}
				subordinatesTree={this.props.subordinatesTree}

				onInitData={this.handleTableInit.bind(this)}
				onEditClick={this.handleEditClick.bind(this)}
				onCopyClick={this.handleCopyClick.bind(this)}

				employeeRelationsWindow={(employe: OptionType) => {

					if (this.props.currentAuthorizationsHiddenEditButton || !employe)
						return null;

					return <UserAuthorizationsWindow
						user={employe}
						rowsPerUser={this.state.rowsPerUsers[employe.value]}
						loading={this.state.loading[employe.value]}
						canInsert={true}
						onInsert={(data) => {
							this.table.current.push(data);
						}}

						blockedRelationIds={() => this.table.current?.getRows().filter(x => !x.deleted && x.relationId).map(x => x.relationId) ?? []}
						getLevelByName={(name: string) => this.getLevelByName(name)}
						tableConfig={this.props.currentAuthorizationsTable}
						config={this.props.config}
					/>
				}}

				changeEmployee={(rawRow: IRelationRowTable, employee: OptionType) => {

					this.loadUsers([employee]);

					let row = rawRow as IAuthorizationRelationData;
					let employeeEntity: IEmployeeEntity = {
						id: parseInt(employee.value),
						name: employee.label,
						kind: "Employee",
						label: employee.label,
						type: null,
					}
					return { ...row, employee: employeeEntity, alert: ALERT.Loading };;
				}}

				onchangedEmployee={(rows, emp) => this.checkAuthorizesInLog(rows as IAuthorizationRelationData[])}
				onChangedEmployees={emp => {
					this.props.onSet({
						employees: emp,
					})
				}}
			/>
			<AuthorizationRelationEditRowDialog
				row={this.state.editingRow}
				resourceName={this.state.editingRow?.resource.label}
				allPermissionLevels={this.state.allAuthorizationLevels}

				config={this.props.config}
				onCancel={() => this.setState({
					editingRow: null,
				})}
				onSaved={(data) => {
					if (this.state.editingRow.copy) {
						let itemsToPush: IAuthorizationRelationRawData = {
							employee: this.state.editingRow.employee,
							from: data.from,
							to: data.to,
							resource: this.state.editingRow.resource,
							level: data.level,
							errorMessages: null,
						};

						this.checkAuthorizesInLog([itemsToPush] as IAuthorizationRelationData[]);
						this.table.current.push(itemsToPush);
					}
					else {
						this.edit(data);
					}

					this.setState({
						editingRow: null,
					});
				}}
			/>
		</div>
	}

	edit(data: IEditRowControlResult) {
		let rows: IAuthorizationRelationData[] = this.table.current.getRows();
		for (let i = 0; i < rows.length; i++) {
			let row = rows[i];
			if (!row.deleted && row.key == this.state.editingRow.key) {
				
				rows[i] = {
					...rows[i],
					...data,
					alert: ALERT.Loading,
					errorMessages: null,
				};
				break;
			}
		}
		this.checkAuthorizesInLog(rows);
		this.table.current.setRows(rows);
	}
}